<template>
	<div class="ai-tools-container">
		<ModalHeader :title="getString('ai_x_reader', [userStore.menuWord])" />
		<div class="row upload-row">
			<p>{{ getString('ai_tools_phrase', [userStore.menuWord]) }}</p>
		</div>
		<div class="row animation-row">
			<div id="ai-tools-generation-anim"></div>
			<div class="dpz-div">
				<AppDropzone
					:class="{disabled: loading}"
					class="menu-dpz"
					is-ai
					:max-bytes="5 * 1000 * 1024"
					:max-width="5000"
					:max-height="5000"
					:allow-pdf="true"
					@upload-complete="fileUploadComplete"
					ref="dpz"
				/>
			</div>
		</div>
		<p v-show="loading" class="mt-8">{{ getString('generating_result') }}</p>
		<div class="row space-between result-row" v-if="responseResult">
			<p class="title">{{ getString('result') }}</p>
			<p class="data result">{{ responseResult }}</p>
		</div>
	</div>
</template>

<script>
import AppDropzone from "@/shared/components/AppDropzone.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import API from "@/shared/helpers/API";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import {useNotificationStore, useLanguageStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import {parseMenuAIResponse} from "@/console-new/helpers/menuAI";

export default {
	setup() {
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const menuStore = useMenuStore();
		const languageStore = useLanguageStore();
		return {userStore, showNotification: notificationStore.showNotification, getString: languageStore.getString, menuStore};
	},
	name: "AITools",
	components: {AppButton, ModalHeader, AppDropzone, AppLoader},
	data() {
		return {
			loading: false,
			responseResult: null,
			generationAnimation: null,
			loadedAnimation: false,
			items: [],
		};
	},
	methods: {
		fileUploadComplete(urls) {
			let url = urls[0];
			this.menuStore.aiToolsLoading = true;
			this.generationAnimation.play();

			setTimeout(() => {

				this.loading = true;
				API.init().decodeMenuFile(this.userStore.currBusinessId, url, (response) => {
					if(response && response.result && response.result === "OK" && response.ai_chat_response_id) {
						this.startMenuResponsePolling(response.ai_chat_response_id);
					} else if(response && response?.generating?.ai_chat_response_id) {
						this.showNotification("already-generating-menu", [this.userStore.menuWord]);
						this.startMenuResponsePolling(response.generating.ai_chat_response_id);
					} else {
						this.stopLoading();
						this.showNotification(response?.details === 'rate_limit' ? "menu-fail-rate-limit" : "generic-fail");
					}
				});

			}, 550);
		},
		startMenuResponsePolling(aiChatResponseId) {

			API.init().getMenuResponse(this.userStore.currBusinessId, aiChatResponseId, (response) => {
				if(response && response.result && response.result === "OK" && response.status) {
					if(response.status === "failed") {
						this.showNotification("generic-fail");
						this.stopLoading();
					} else if(response.status === "completed") {
						this.analyzeResults(response.response_data);
					} else {
						setTimeout(() => {
							this.startMenuResponsePolling(aiChatResponseId);
						}, 3000);
					}
				} else {
					this.showNotification("generic-fail");
					this.stopLoading();
				}
			});

		},
		analyzeResults(runResult) {

			let items = parseMenuAIResponse(runResult);

			this.responseResult = this.getString('found_x_items', [items.length]);
			this.items = items;

			if(items.length > 0) {
				this.addItemsToMenu();
			} else {
				this.stopLoading();
				this.showNotification("menu-generation-empty-items", [this.userStore.menuWord]);
			}
		},
		addItemsToMenu() {

			let categories = {};
			for(let i = 0; i < this.items.length; i++) {
				let item = this.items[i];
				if(!categories[item.category]) {
					let newCategory = this.menuStore.addNewCategory({name: item.category});
					newCategory.editActive = false;
					newCategory.editActiveDelayed = false;
					categories[item.category] = newCategory.tempid;
				}
			}

			this.menuStore.saveCategories((success, details) => {
				if(success) {
					for(let i = 0; i < this.items.length; i++) {
						let item = this.items[i];
						if(categories.hasOwnProperty(item.category)) {
							let newItem = this.menuStore.addNewItem(categories[item.category], {
								name: item.name,
								price: item.price,
								ingredients: item.description,
							});
							newItem.active = false;
							newItem.collapsedDelayed = true;
						}
					}
					this.menuStore.saveItems(()=>{
						this.menuStore.refreshMenusData(()=>{
							this.menuStore.aiToolsActive = false;
							this.stopLoading();
							this.showNotification("menu-generation-success-items", [this.items.length, this.userStore.menuWord]);
						});
					});
				} else {
					this.stopLoading();
					this.showNotification("generic-fail");
				}
			});
		},
		loadGeneratingAnimation() {
			if(typeof bodymovin !== 'undefined') {
				const animationPath = 'https://img.qromo.io/img/vue/anim/ai-generation' + (this.userStore.darkMode ? '-dark' : '-light') + '.json';
				if(!this.loadedAnimation) {
					this.generationAnimation = bodymovin.loadAnimation({
						container: document.getElementById('ai-tools-generation-anim'),
						renderer: 'svg',
						loop: true,
						autoplay: false,
						path: animationPath,
						rendererSettings: {
							preserveAspectRatio: 'xMidYMid meet'
						}
					});
					this.loadedAnimation = true;
				}
			}
		},
		stopLoading() {
			this.loading = false;
			this.menuStore.aiToolsLoading = false;
			if(this.generationAnimation) {
				this.generationAnimation.stop();
			}
			if(this.$refs['dpz']) {
				this.$refs['dpz'].dropzoneObj.removeAllFiles()
				this.$refs['dpz'].currentFiles = [];
			}
		},
	},
	mounted() {
		this.loadGeneratingAnimation();
	},
	beforeUnmount() {
		if(this.generationAnimation) {
			this.generationAnimation.destroy();
		}
	}
}
</script>

<style scoped lang="scss">
.upload-row {
	margin-top: 12px;
	margin-bottom: 20px;
	gap: 12px;
}
.result-row {
	border-bottom: 1px solid $primary;
	padding: 8px 0;
	gap: 12px;

	&:last-child {
		border-bottom: none;
	}

	.title {
		font-weight: 500;
		color: #9e9e9e;
		font-size: 14px;
	}
	.data {
		margin-left: auto;
		font-size: 14px;
	}
	.result {
		font-size: 16px;
		font-weight: 700;
	}
}
#ai-tools-generation-anim {
	width: 100%;
	max-width: calc(100% - 300px);
	margin: 0 auto;
	height: fit-content;
	position: relative;
	:deep(svg) {
		height: auto !important;
	}

	@media (max-width: 1080px) {
		max-width: calc(100% - 210px);
	}
}
@media (max-width: 768px) {
	.animation-row {
		@include centered;
		gap: 20px;
		flex-direction: column-reverse;
	}
	#ai-tools-generation-anim {
		max-width: 100%;
	}
	.dpz-div {
		width: 100%;
	}
}
.menu-dpz.dropzone-container {
	height: 100%;
	width: 220px;

	:deep(.dropzone-wrapper) {
		height: 100%;
		width: 100%;
		.dropzone {
			width: 100%;
			height: 100%;
		}
	}
	@media (max-width: 1080px) {
		width: 150px;
	}
	@media (max-width: 768px) {
		width: 100%;
		height: 150px;
	}
}
</style>